import React, { useRef } from 'react';
import { Reorder } from 'framer-motion';
import { stickerTextStyle } from './StickerText';

interface DraggableTabProps {
    item: string;
    label: string;
    isActive: boolean;
    onClick: () => void;
    color: string;
    isReorderMode: boolean;
    onEnterReorderMode: () => void;
    onExitReorderMode: () => void;
}

export const DraggableTab: React.FC<DraggableTabProps> = ({
    item, label, isActive, onClick, color, isReorderMode, onEnterReorderMode, onExitReorderMode
}) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const startPosRef = useRef<{ x: number; y: number } | null>(null);
    const cancelledRef = useRef(false);

    const LONG_PRESS_MS = 350;
    const MOVE_THRESHOLD = 10;

    const clearTimer = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const handlePointerDown = (e: React.PointerEvent) => {
        if (isReorderMode) return; // In reorder mode, framer-motion handles down events directly

        startPosRef.current = { x: e.clientX, y: e.clientY };
        cancelledRef.current = false;

        timeoutRef.current = setTimeout(() => {
            if (!cancelledRef.current) {
                onEnterReorderMode();
                if (navigator.vibrate) navigator.vibrate(50);
            }
        }, LONG_PRESS_MS);
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (isReorderMode) return;
        if (!startPosRef.current || cancelledRef.current) return;

        const dx = Math.abs(e.clientX - startPosRef.current.x);
        const dy = Math.abs(e.clientY - startPosRef.current.y);

        if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
            cancelledRef.current = true;
            clearTimer();
        }
    };

    const handlePointerUpOrCancel = () => {
        if (!isReorderMode) {
            clearTimer();
            startPosRef.current = null;
        }
    };

    return (
        <Reorder.Item
            value={item}
            dragListener={isReorderMode}
            className={`flex-shrink-0 relative select-none ${isReorderMode ? 'opacity-80' : ''}`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUpOrCancel}
            onPointerCancel={handlePointerUpOrCancel}
            onPointerLeave={handlePointerUpOrCancel}
            whileDrag={{ scale: 1.05, zIndex: 50, opacity: 1 }}
        >
            <button
                onClick={(e) => {
                    if (isReorderMode) {
                        e.preventDefault();
                        e.stopPropagation();
                        onExitReorderMode();
                    } else {
                        onClick();
                    }
                }}
                style={{
                    color: color,
                    borderColor: isActive && !isReorderMode ? color : 'transparent',
                    ...stickerTextStyle(0.18)
                }}
                className={`py-3 px-1 inline-flex items-center text-sm font-bold transition-all duration-200 border-b-2 whitespace-nowrap ${isActive && !isReorderMode
                    ? ''
                    : 'hover:opacity-100 hover:border-gray-700'
                    } ${isReorderMode ? 'animate-tab-jiggle' : ''}`}
            >
                {label}
            </button>
        </Reorder.Item>
    );
};
